import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Disc3, Mic2, Headphones } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const heroImg = "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", bio: "", is_artist: true });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await register(form);
    setBusy(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  const inputCls = "w-full bg-zinc-900/60 border border-white/10 text-white placeholder-zinc-500 px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFD60A] focus:border-[#FFD60A]";

  return (
    <div className="min-h-[calc(100vh-6.5rem)] grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-16 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8">
            <Disc3 className="h-7 w-7 text-[#FFD60A]" />
            <span className="font-brand text-2xl text-[#FFD60A]">NEXTtt<sup className="text-[0.5em] align-super ml-0.5">™</sup></span>
          </div>
          <h1 className="font-display text-3xl font-bold text-white">Join the movement</h1>
          <p className="text-zinc-400 mt-1 mb-8">Create your account to watch, like, comment — and, as an artist, share your music.</p>

          {error && <div data-testid="register-error" className="mb-4 bg-[#FFD60A]/10 border border-[#FFD60A]/40 text-[#FFD60A] text-sm px-4 py-3">{error}</div>}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <p className="text-sm font-semibold text-white mb-2">I'm joining as a…</p>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" data-testid="role-artist" onClick={() => setForm({ ...form, is_artist: true })}
                  className={`text-left px-4 py-3 border transition-colors ${form.is_artist ? "border-[#FFD60A] bg-[#FFD60A]/10" : "border-white/10 bg-zinc-900/40 hover:border-white/30"}`}>
                  <Mic2 className={`h-5 w-5 mb-1.5 ${form.is_artist ? "text-[#FFD60A]" : "text-zinc-400"}`} />
                  <span className="block font-semibold text-white text-sm">Music Artist</span>
                  <span className="block text-xs text-zinc-400 mt-0.5">Post & share your music</span>
                </button>
                <button type="button" data-testid="role-fan" onClick={() => setForm({ ...form, is_artist: false })}
                  className={`text-left px-4 py-3 border transition-colors ${!form.is_artist ? "border-[#FFD60A] bg-[#FFD60A]/10" : "border-white/10 bg-zinc-900/40 hover:border-white/30"}`}>
                  <Headphones className={`h-5 w-5 mb-1.5 ${!form.is_artist ? "text-[#FFD60A]" : "text-zinc-400"}`} />
                  <span className="block font-semibold text-white text-sm">Fan / Listener</span>
                  <span className="block text-xs text-zinc-400 mt-0.5">Watch, like & comment</span>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-white mb-1">
                {form.is_artist ? "Stage name" : "Display name"}
                {form.is_artist && <span className="text-[#FFD60A]"> *</span>}
              </label>
              <input data-testid="register-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={form.is_artist ? "e.g. Hoodi_X" : "Your name"} className={inputCls} required />
              {form.is_artist && <p className="text-xs text-zinc-500 mt-1.5">Required — every video you post is labelled with this stage name.</p>}
            </div>
            <input data-testid="register-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={inputCls} required />
            <input data-testid="register-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password (min 8 chars, 1 letter + 1 number)" className={inputCls} required minLength={8} />
            <textarea data-testid="register-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder={form.is_artist ? "Short artist bio (optional)" : "Short bio (optional)"} rows={2} className={`${inputCls} resize-none`} />

            <button type="submit" disabled={busy} data-testid="register-submit" className="w-full bg-[#FFD60A] text-black py-3 font-semibold hover:bg-[#FFD60A]/90 transition-colors disabled:opacity-60">
              {busy ? "Creating…" : form.is_artist ? "Create artist account" : "Create account"}
            </button>
          </form>

          <p className="text-sm text-zinc-400 mt-6">Already have an account? <Link to="/login" className="text-[#FFD60A] font-semibold hover:underline">Log in</Link></p>
        </div>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/30" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-display text-4xl font-black tracking-tighter text-white">Your audience is waiting.</h2>
        </div>
      </div>
    </div>
  );
};

export default Register;
